import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

/**
 * B76 — Pannello Esito (Fase 1 del ciclo chiuso).
 *
 * SCOPE: SOLO cattura del dato. Non calcolo, non apprendimento.
 * Visibile SOLO dopo analisi completata (scores presenti).
 *
 * Props:
 *  - subjectType: 'candidate' | 'employee'
 *  - subjectId: string
 *  - hasScores: bool  (indica se l'analisi e' completata)
 *  - initialOutcome: object|null
 */
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RESA_OPTIONS = [1, 2, 3, 4, 5];
const EXIT_REASONS = [
  { value: "dimissioni_volontarie", label: "Dimissioni volontarie" },
  { value: "licenziamento", label: "Licenziamento" },
  { value: "fine_contratto", label: "Fine contratto" },
  { value: "altro", label: "Altro" },
];

function ResaSelector({ value, onChange, label, testId }) {
  // 5 pulsanti circolari 1-5 con hint. "resa nel ruolo", NON "performance".
  return (
    <div className="flex flex-col gap-1.5" data-testid={testId}>
      <div className="text-[13px] font-medium text-[#0D1F3C]/80">{label}</div>
      <div className="flex items-center gap-1.5">
        {RESA_OPTIONS.map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => onChange(value === n ? null : n)}
            className={`w-8 h-8 rounded-full text-[12px] font-semibold border transition-all ${
              value === n
                ? "bg-[#1B4FD8] text-white border-[#1B4FD8]"
                : "bg-white text-[#0D1F3C]/70 border-[#0D1F3C]/20 hover:border-[#1B4FD8]/60"
            }`}
            data-testid={`${testId}-${n}`}
          >
            {n}
          </button>
        ))}
        {value != null && (
          <button
            type="button"
            onClick={() => onChange(null)}
            className="text-[11px] text-[#0D1F3C]/50 ml-1 hover:text-[#0D1F3C]"
            data-testid={`${testId}-clear`}
          >
            Azzera
          </button>
        )}
      </div>
      <div className="text-[10.5px] text-[#0D1F3C]/50">1 = molto sotto le attese · 5 = molto oltre le attese</div>
    </div>
  );
}

export default function OutcomePanel({ subjectType, subjectId, hasScores, initialOutcome }) {
  const [outcome, setOutcome] = useState(initialOutcome || {});
  const [saving, setSaving] = useState(false);
  const isCandidate = subjectType === "candidate";

  useEffect(() => {
    setOutcome(initialOutcome || {});
  }, [initialOutcome, subjectId]);

  const patch = useCallback(async (delta) => {
    if (!hasScores) return;
    setSaving(true);
    try {
      const url = `${API}/${isCandidate ? "candidates" : "employees"}/${subjectId}/outcome`;
      const res = await axios.put(url, { ...outcome, ...delta });
      setOutcome(res.data?.outcome || {});
      toast.success("Esito aggiornato");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore durante il salvataggio");
    } finally {
      setSaving(false);
    }
  }, [outcome, subjectId, subjectType, hasScores, isCandidate]);

  if (!hasScores) {
    return (
      <div className="rounded-lg border border-[#0D1F3C]/10 bg-[#0D1F3C]/5 p-4 text-sm text-[#0D1F3C]/60"
           data-testid="outcome-panel-blocked">
        L'esito potra essere registrato dopo il completamento dell'analisi.
      </div>
    );
  }

  const hiredKnown = outcome?.hired === true || outcome?.hired === false;
  const showResaBlock = isCandidate ? outcome?.hired === true : true;
  const isUscito = outcome?.retention_status === "uscito";

  return (
    <div className="rounded-xl border border-[#0D1F3C]/10 bg-white p-5 space-y-5" data-testid="outcome-panel">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[15px] font-semibold text-[#0D1F3C]">Esito</div>
          <div className="text-[12px] text-[#0D1F3C]/60">
            Registrazione andamento reale nel ruolo. Uso interno HR.
          </div>
        </div>
        {saving && <Badge className="bg-[#1B4FD8]/10 text-[#1B4FD8] border-0 text-[11px]">Salvataggio...</Badge>}
      </div>

      {/* Candidato: Assunto si/no + hire_date */}
      {isCandidate && (
        <div className="grid gap-4 md:grid-cols-2 grid-cols-1">
          <div className="flex items-center justify-between rounded-lg border border-[#0D1F3C]/10 p-3">
            <div>
              <div className="text-[13px] font-medium text-[#0D1F3C]">Assunto</div>
              <div className="text-[11px] text-[#0D1F3C]/55">Stato del rapporto</div>
            </div>
            <Switch
              checked={outcome?.hired === true}
              onCheckedChange={(v) => patch({ hired: v })}
              data-testid="outcome-hired-switch"
            />
          </div>
          {hiredKnown && outcome?.hired === true && (
            <div className="flex flex-col gap-1.5">
              <label className="text-[13px] font-medium text-[#0D1F3C]/80">Data assunzione</label>
              <Input
                type="date"
                value={outcome?.hire_date || ""}
                onChange={(e) => setOutcome({ ...outcome, hire_date: e.target.value })}
                onBlur={(e) => patch({ hire_date: e.target.value })}
                data-testid="outcome-hire-date"
              />
            </div>
          )}
        </div>
      )}

      {/* Dipendente: hire_date derivata dalla valutazione (readonly hint) */}
      {!isCandidate && outcome?.hire_date && (
        <div className="text-[12px] text-[#0D1F3C]/60">
          Data valutazione: <strong>{outcome.hire_date}</strong>
        </div>
      )}

      {/* Resa 3/6/12 */}
      {showResaBlock && (
        <div className="grid gap-4 md:grid-cols-3 grid-cols-1" data-testid="outcome-resa-block">
          <ResaSelector
            testId="outcome-resa-3m"
            label="Resa nel ruolo (3 mesi)"
            value={outcome?.resa_3m ?? null}
            onChange={(v) => patch({ resa_3m: v })}
          />
          <ResaSelector
            testId="outcome-resa-6m"
            label="Resa nel ruolo (6 mesi)"
            value={outcome?.resa_6m ?? null}
            onChange={(v) => patch({ resa_6m: v })}
          />
          <ResaSelector
            testId="outcome-resa-12m"
            label="Resa nel ruolo (12 mesi)"
            value={outcome?.resa_12m ?? null}
            onChange={(v) => patch({ resa_12m: v })}
          />
        </div>
      )}

      {/* Retention status + uscita */}
      {showResaBlock && (
        <div className="grid gap-4 md:grid-cols-3 grid-cols-1" data-testid="outcome-retention-block">
          <div className="flex flex-col gap-1.5">
            <label className="text-[13px] font-medium text-[#0D1F3C]/80">Stato permanenza</label>
            <Select
              value={outcome?.retention_status || ""}
              onValueChange={(v) => patch({ retention_status: v || null })}
            >
              <SelectTrigger data-testid="outcome-retention-status">
                <SelectValue placeholder="Seleziona..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="attivo">Attivo</SelectItem>
                <SelectItem value="uscito">Uscito</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {isUscito && (
            <>
              <div className="flex flex-col gap-1.5">
                <label className="text-[13px] font-medium text-[#0D1F3C]/80">Data uscita</label>
                <Input
                  type="date"
                  value={outcome?.exit_date || ""}
                  onChange={(e) => setOutcome({ ...outcome, exit_date: e.target.value })}
                  onBlur={(e) => patch({ exit_date: e.target.value })}
                  data-testid="outcome-exit-date"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-[13px] font-medium text-[#0D1F3C]/80">Motivo uscita</label>
                <Select
                  value={outcome?.exit_reason || ""}
                  onValueChange={(v) => patch({ exit_reason: v || null })}
                >
                  <SelectTrigger data-testid="outcome-exit-reason">
                    <SelectValue placeholder="Seleziona..." />
                  </SelectTrigger>
                  <SelectContent>
                    {EXIT_REASONS.map((r) => (
                      <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </>
          )}
        </div>
      )}

      {outcome?.scores_snapshot_hash && (
        <div className="text-[10.5px] text-[#0D1F3C]/45 pt-2 border-t border-[#0D1F3C]/10 font-mono">
          Snapshot analisi: {outcome.scores_snapshot_hash.slice(0, 12)}...
        </div>
      )}
    </div>
  );
}
